import React, { useState, useEffect, useRef } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  InputAdornment,
  IconButton,
  makeStyles,
  TextField,
} from "@material-ui/core";
import brandTheme from "brandTheme/Theme";
import DateRange from "@material-ui/icons/DateRange";
import { parseDate } from "utils/Utils";

const formatDateDisplay = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const parseInputDate = (input) => {
  // Accept only DD/MM/YYYY
  const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;

  if (!regex.test(input)) return null;

  const [_, day, month, year] = input.match(regex);

  const isoFormat = `${year}-${month}-${day}`;

  const dateObj = new Date(isoFormat);

  return isNaN(dateObj.getTime()) ? null : dateObj;
};


const useStyles = makeStyles((theme) => ({
  inputBox: {
    marginBottom: "1%",
    position: "relative",
    "& .MuiFormLabel-root.Mui-disabled": {
      color: brandTheme.lightBlack,
    },
    "& .MuiOutlinedInput-inputAdornedEnd": {
      padding: "11px",
    },
     '& .MuiInputLabel-outlined':{
      margin:'-6px',
      color:'#000000'
    },
    '& .MuiFormLabel-filled':{
      margin:'0px'
    },
   
  },
  calendarPopup: {
    position: "absolute",
    zIndex: 1000,
    background: "white",
    boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",
    borderRadius: "8px",
    padding: "10px",
  },
}));

const CustomDateField = ({
  label,
  value,
  onChange,
  className,
  disabled,
  minDate,
  maxDate,
}) => {
  const [date, setDate] = useState(value ? new Date(value) : null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const inputRef = useRef(null);
  const classes = useStyles();
  const [inputText, setInputText] = useState(
    value ? formatDateDisplay(value) : ""
  );
  const [error, setError] = useState("");
  useEffect(() => {
  if (value) {
    const d = new Date(value);
    setDate(d);
    setInputText(formatDateDisplay(d));
  }
}, [value]);
  const formatWithSlash = (value) => {
  // Remove everything except numbers
  let v = value.replace(/[^0-9]/g, "");

  if (v.length > 2) v = v.slice(0, 2) + "/" + v.slice(2);
  if (v.length > 5) v = v.slice(0, 5) + "/" + v.slice(5, 9);

  return v;
};
const isValidDate = (str) => {
  const [day, month, year] = str.split("/").map(Number);

  const d = new Date(`${year}-${month}-${day}`);

  return (
    d &&
    d.getDate() === day &&
    d.getMonth() + 1 === month &&
    d.getFullYear() === year
  );
};

  // ✅ Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  const handleDateChange = (newDate) => {
    setDate(newDate);
    setInputText(formatDateDisplay(newDate));
    setShowCalendar(false);
    setError("");

    const formattedDate = parseDate(newDate);
    onChange(formattedDate);
  };

  const handleInputChange = (event) => {
  let inputValue = event.target.value;

  // ✅ Auto slash & block alphabets
  inputValue = formatWithSlash(inputValue);

  setInputText(inputValue);

  // Only validate when full date typed
  if (inputValue.length === 10) {
    if (!isValidDate(inputValue)) {
      setError("Invalid Date");
      setDate(null);
      onChange("");
      return;
    }

    const [day, month, year] = inputValue.split("/");
    const newDate = new Date(`${year}-${month}-${day}`);

    // Min Max check
    if (
      (minDate && newDate < minDate) ||
      (maxDate && newDate > maxDate)
    ) {
      setError("Date out of allowed range");
      setDate(null);
      onChange("");
      return;
    }

    setError("");
    setDate(newDate);

    const formatted = parseDate(newDate);
    onChange(formatted);
  } else {
    setError("");
    setDate(null);
    onChange("");
  }
};

  const toggleCalendar = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const calendarHeight = 300; // approx height of calendar
      const fitsBelow = rect.bottom + calendarHeight < windowHeight;

      setCalendarPosition({
        top: fitsBelow ? rect.height + 4 : -calendarHeight - 4,
        left: 0,
      });
    }
    setShowCalendar((prev) => !prev);
  };

  return (
    <div className={classes.inputBox} ref={inputRef}>
      <TextField
        label={label}
        value={inputText}
        onChange={handleInputChange}
        fullWidth
        variant="outlined"
        disabled={disabled}
        error={!!error}
        helperText={error}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={toggleCalendar} disabled={disabled}>
                <DateRange />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {showCalendar && (
        <div
          className={classes.calendarPopup}
          style={{
            top: calendarPosition.top,
            left: calendarPosition.left,
          }}
        >
          <Calendar
            onChange={handleDateChange}
            value={date}
            className={className}
            minDate={minDate}
            maxDate={maxDate}
            defaultActiveStartDate={
              date ? date : maxDate ? maxDate : new Date()
            }
          />
        </div>
      )}
    </div>
  );
};

export default CustomDateField;